/**
 * Dialog "Modifica prezzi in massa" per N materiali selezionati.
 *
 * Tre modalità (radio):
 *
 *  1. Aumenta listino di X% — moltiplica `list_price` per (1 + pct/100).
 *     Il netto si ricalcola automaticamente via trigger DB. Utile dopo
 *     un cambio listino fornitore generalizzato (es. +3% inflazione).
 *
 *  2. Imposta sconto extra a X% — setta `extra_discount` (NON cumulativo
 *     con se' stesso, sostituisce). Utile per "applico 10% di extra a
 *     tutta la famiglia lastre Knauf".
 *
 *  3. Aumenta sconto extra di X punti — somma X all'extra esistente
 *     (`extra_discount = extra_discount + X`). Utile per ritocchi.
 *
 * Conferma esplicita prima di applicare. Trigger DB ricalcola unit_price
 * e propaga via la view materials_with_pricing.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TrendingUp, Percent, Plus, AlertTriangle } from 'lucide-react';

type Mode = 'list_pct' | 'extra_set' | 'extra_add';

interface BulkPriceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSuccess?: () => void;
}

const BulkPriceEditDialog: React.FC<BulkPriceEditDialogProps> = ({
  open, onOpenChange, selectedIds, onSuccess,
}) => {
  const [mode, setMode] = useState<Mode>('list_pct');
  const [valueStr, setValueStr] = useState<string>('5');
  const queryClient = useQueryClient();

  const value = Number(valueStr);
  const validNumber = !valueStr || Number.isFinite(value);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!Number.isFinite(value)) throw new Error('Valore non valido');
      if (selectedIds.length === 0) throw new Error('Nessun materiale selezionato');

      // Strategie diverse perche' Postgres non ha un "UPDATE ... SET col = col * X
      // WHERE id IN (...)" via PostgREST client. Carichiamo, calcoliamo, scriviamo.
      const { data: rows, error: readErr } = await supabase
        .from('materials')
        .select('id, list_price, extra_discount')
        .in('id', selectedIds);
      if (readErr) throw readErr;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updates: Promise<any>[] = [];
      let skipped = 0;
      for (const r of rows ?? []) {
        const item = r as { id: string; list_price?: number | null; extra_discount?: number | null };
        let patch: Record<string, number> | null = null;
        if (mode === 'list_pct') {
          if (item.list_price == null) { skipped += 1; continue; }
          const next = Math.round(item.list_price * (1 + value / 100) * 10000) / 10000;
          patch = { list_price: next };
        } else if (mode === 'extra_set') {
          patch = { extra_discount: Math.max(0, Math.min(100, value)) };
        } else if (mode === 'extra_add') {
          const cur = item.extra_discount ?? 0;
          patch = { extra_discount: Math.max(0, Math.min(100, cur + value)) };
        }
        if (patch) {
          updates.push(
            supabase.from('materials').update(patch).eq('id', item.id),
          );
        }
      }
      const results = await Promise.all(updates);
      const failed = results.filter((r) => r.error).length;
      return { ok: updates.length - failed, failed, skipped, total: selectedIds.length };
    },
    onSuccess: ({ ok, failed, skipped, total }) => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      const parts: string[] = [`${ok} materiali aggiornati`];
      if (failed > 0) parts.push(`${failed} errori`);
      if (skipped > 0) parts.push(`${skipped} saltati (senza listino)`);
      toast.success(`${parts.join(' · ')} (su ${total})`);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      console.error('[BulkPriceEdit] error:', err);
      toast.error(err.message || 'Errore durante la modifica');
    },
  });

  const previewLabel = (() => {
    if (!validNumber) return '';
    if (mode === 'list_pct') {
      if (value === 0) return 'Nessuna modifica al listino';
      return `Listino × ${(1 + value / 100).toFixed(4)} (${value > 0 ? '+' : ''}${value}%)`;
    }
    if (mode === 'extra_set') return `Sconto extra impostato a ${value}%`;
    if (mode === 'extra_add') return `Sconto extra: corrente ${value > 0 ? '+' : ''}${value} punti`;
    return '';
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifica prezzi in massa</DialogTitle>
          <DialogDescription>
            Azione applicata a {selectedIds.length} {selectedIds.length === 1 ? 'materiale selezionato' : 'materiali selezionati'}.
            Il netto si ricalcola automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Modalità</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as Mode)}
              className="mt-2 space-y-2"
            >
              <label className="flex items-start gap-2 p-2 rounded border hover:bg-muted/40 cursor-pointer">
                <RadioGroupItem value="list_pct" id="bulk-list_pct" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <TrendingUp className="h-3.5 w-3.5" />
                    Aumenta listino di X%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Moltiplica `list_price` × (1+X/100). Usalo dopo un rincaro fornitore.
                    Skip se manca il listino.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 rounded border hover:bg-muted/40 cursor-pointer">
                <RadioGroupItem value="extra_set" id="bulk-extra_set" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Percent className="h-3.5 w-3.5" />
                    Imposta sconto extra a X%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sostituisce l'`extra_discount` corrente. Clamp 0–100. Lo sconto
                    famiglia non viene toccato.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 rounded border hover:bg-muted/40 cursor-pointer">
                <RadioGroupItem value="extra_add" id="bulk-extra_add" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Plus className="h-3.5 w-3.5" />
                    Aumenta sconto extra di X punti
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Somma X all'extra esistente. Usa numero negativo per diminuire.
                    Clamp 0–100.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="bulk-value" className="text-xs uppercase tracking-wider text-muted-foreground">
              Valore X
            </Label>
            <Input
              id="bulk-value"
              type="number"
              step="0.1"
              value={valueStr}
              onChange={(e) => setValueStr(e.target.value)}
              className="mt-1"
              placeholder="es. 5"
            />
            {previewLabel && (
              <p className="mt-2 text-sm text-muted-foreground italic">{previewLabel}</p>
            )}
          </div>

          {mode === 'extra_set' && (
            <div className="flex items-start gap-2 p-3 rounded bg-amber-50 text-amber-900 text-xs border border-amber-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Sovrascrive lo sconto extra esistente. Se vuoi sommare invece di sostituire,
                usa la modalità precedente.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !validNumber || selectedIds.length === 0}
          >
            {mutation.isPending ? 'Applico…' : `Applica a ${selectedIds.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPriceEditDialog;
